import { Link, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useLocation, addressLocation } from "@/contexts/LocationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlacesAutocomplete } from "@/components/PlacesAutocomplete";
import { currentPosition, nameForPoint } from "@/lib/googleMaps";
import { MenuRow, SiteTabs } from "@/components/MegaNav";
import { SiteSwitcher } from "@/components/PawBazaarSites";
import { ACCOUNT_LINKS, siteUrl } from "@/lib/sites";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingBag, User, LogOut, LayoutDashboard, Bell, PawPrint, MapPin, Loader2, ChevronDown, LocateFixed, Search } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import usePolling from "@/hooks/usePolling";
import { toast } from "sonner";

import { logger } from "@/lib/logger";
const SHOPPING = /^\/($|shop|browse|vendors?\b|product\/|cart)/;
const STAFF = ["admin", "support", "vendor", "salon", "ngo", "delivery_partner"];
const BOT = /bot|crawl|spider|slurp|preview|lighthouse/i;

export default function Nav() {
  const { user, logout } = useAuth();
  const cart = useCart();
  const { mustChoose, setLocation, locationLabel, skipChoice, suggestion, acceptSuggestion, keepPlace } = useLocation();
  const { pathname } = useRouterLocation();
  const here = pathname.startsWith("/learn") ? "learn" : "shop";
  // First visit: shoppers pick where to deliver before seeing shops (no silent
  // default area). Only on shopping pages, not for staff, not for search bots.
  const required = mustChoose && SHOPPING.test(pathname) && !STAFF.includes(user?.role) && !BOT.test(navigator.userAgent || "");
  const navigate = useNavigate();
  const [notifCount, setNotifCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [locOpen, setLocOpen] = useState(false);
  const [navQ, setNavQ] = useState("");
  // Other pages (landing banner, product page) can open the location popup.
  useEffect(() => {
    const openIt = () => setLocOpen(true);
    window.addEventListener("pm:open-location", openIt);
    return () => window.removeEventListener("pm:open-location", openIt);
  }, []);

  // Opened because a location is needed: close it once one is chosen.
  const forcedOpen = useRef(false);
  useEffect(() => {
    if (required) { forcedOpen.current = true; setLocOpen(true); }
    else if (forcedOpen.current) { forcedOpen.current = false; setLocOpen(false); }
  }, [required]);
  // The cookie banner waits until the location is chosen (it can't be clicked behind this window).
  useEffect(() => {
    window.pbLocationRequired = required;
    window.dispatchEvent(new Event("pb:location-required"));
  }, [required]);

  const latestSeen = useRef(null);
  useEffect(() => { if (!user) { setNotifs([]); setNotifCount(0); latestSeen.current = null; } }, [user]);
  usePolling(async () => {
    if (!user) return;
    const { data } = await api.get("/notifications/unread-count");
    setNotifCount(data.unread || 0);
    if (data.latest_at !== latestSeen.current) {
      latestSeen.current = data.latest_at;
      const { data: list } = await api.get("/notifications");
      setNotifs(list);
    }
  }, 60000, [user?.id]);

  const markRead = async () => {
    try { await api.post("/notifications/mark-read"); setNotifCount(0); } catch (e) { logger.warn("Notification mark-read failed:", e); }
  };

  // The Operations Console is a separate app mounted at /console (its own
  // domain when deployed). Internal-role links must leave the customer SPA.
  const CONSOLE_URL = process.env.REACT_APP_CONSOLE_URL || "/console";

  // Phase 11: resolve a click destination for a notification based on its kind/role.
  const notifLink = (n) => {
    if (n.link) return n.link;
    const k = n.kind || "";
    if (user?.role === "vendor") {
      if (k.startsWith("delivery") || k.startsWith("order") || k === "new_order") return `${CONSOLE_URL}/vendor`;
      if (k === "vendor") return `${CONSOLE_URL}/vendor`;
    }
    if (user?.role === "delivery_partner") {
      if (k.startsWith("delivery")) return `${CONSOLE_URL}/delivery-partner`;
      return null;
    }
    if (user?.role === "support") return `${CONSOLE_URL}/support`;
    if (user?.role === "admin") return `${CONSOLE_URL}/admin`;
    // customer (default)
    if (k.startsWith("subscription")) return "/account";
    if (k.startsWith("order") || k.startsWith("delivery") || k === "feedback" || k === "new_order") return "/account";
    if (k === "vendor") return null;
    return null;
  };

  const dashboardPath = user?.role === "vendor" ? `${CONSOLE_URL}/vendor` : user?.role === "admin" ? `${CONSOLE_URL}/admin` : user?.role === "support" ? `${CONSOLE_URL}/support` : user?.role === "delivery_partner" ? `${CONSOLE_URL}/delivery-partner` : "/account";
  // Cross-app links (console paths or absolute URLs) need a full navigation.
  const goTo = (link) => {
    if (!link) return;
    if (link.startsWith(CONSOLE_URL) || link.startsWith("http")) window.location.href = link;
    else navigate(link);
  };
  const isCustomerContext = !user || user?.role === "customer";
  const search = (e) => { e.preventDefault(); navigate(navQ.trim() ? `/shop?q=${encodeURIComponent(navQ.trim())}` : "/shop"); };
  const searchBox = (cls, testid) => (
    <form onSubmit={search} role="search" className={`items-center gap-2 h-10 px-4 rounded-full border border-stone-300 bg-white focus-within:border-stoneWarm min-w-0 ${cls}`} data-testid={testid}>
      <Search className="h-4 w-4 text-stone-400 shrink-0" aria-hidden />
      <label htmlFor={`${testid}-q`} className="sr-only">Search products, services or NGOs</label>
      <input id={`${testid}-q`} value={navQ} onChange={(e) => setNavQ(e.target.value)} placeholder="Search products, services or NGOs" className="flex-1 min-w-0 bg-transparent text-sm outline-none" />
    </form>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/95 backdrop-blur-xl">
      {isCustomerContext && <div className="lg:hidden"><SiteSwitcher current={here} /></div>}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group shrink-0">
          <span className="h-9 w-9 rounded-xl bg-marigold flex items-center justify-center group-hover:rotate-6 transition-transform">
            <PawPrint className="h-5 w-5 text-stoneWarm" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-lg font-semibold tracking-tight max-[400px]:sr-only">PawBazaar</span>
        </Link>

        {isCustomerContext ? <SiteTabs current={here} className="hidden lg:flex shrink-0" /> : (
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/shop" data-testid="nav-browse" className="px-4 py-2 rounded-full text-sm font-medium hover:bg-stone-100 transition-colors">Shop</Link>
          </nav>
        )}
        {isCustomerContext && searchBox("hidden md:flex flex-1 max-w-xl", "nav-search")}
        <div className="flex-1 md:hidden" />

        <div className="flex items-center gap-1 sm:gap-2 ml-auto shrink-0">
          {isCustomerContext && (
            <button
              onClick={() => setLocOpen(true)}
              data-testid="nav-location-chip"
              className="inline-flex items-center gap-1 h-9 px-2.5 sm:px-3 rounded-full text-xs sm:text-sm font-semibold bg-teal/10 text-teal hover:bg-teal/20 border border-teal/30 max-w-[7.5rem] sm:max-w-[11rem]"
              title="Change delivery location"
              aria-label={`Delivering to ${locationLabel}. Change delivery location`}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{locationLabel}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70 hidden sm:block" aria-hidden />
            </button>
          )}

          {isCustomerContext && (
            <button type="button" onClick={() => navigate("/cart")} data-testid="nav-cart-btn" aria-label={`Cart${cart.count ? `, ${cart.count} items` : ""}`}
              className="relative h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-stone-100">
              <ShoppingBag className="h-5 w-5" />
              {cart.count > 0 && (
                <span className="absolute top-0.5 right-0 bg-terracotta text-white text-[10px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                  {cart.count}
                </span>
              )}
            </button>
          )}

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" onClick={markRead} data-testid="nav-notif-btn" aria-label="Notifications" className="relative h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-stone-100">
                  <Bell className="h-5 w-5" />
                  {notifCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 bg-terracotta text-white text-[9px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                      {notifCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-auto">
                <DropdownMenuLabel className="font-heading">Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="px-3 py-4 text-sm text-stone-500">No notifications yet</div>}
                {notifs.slice(0, 20).map(n => {
                  const link = notifLink(n);
                  return link ? (
                    <button
                      key={n.id}
                      onClick={() => goTo(link)}
                      data-testid={`notif-item-${n.id}`}
                      className="w-full text-left px-3 py-2 border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors"
                    >
                      <div className="text-sm">{n.message}</div>
                      <div className="text-[10px] text-stone-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </button>
                  ) : (
                    <div key={n.id} data-testid={`notif-item-${n.id}`} className="px-3 py-2 border-b border-stone-100 last:border-0">
                      <div className="text-sm">{n.message}</div>
                      <div className="text-[10px] text-stone-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {!user ? (
            <Button size="sm" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 px-4">Sign in</Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="nav-user-menu" aria-label="My account" className="flex items-center gap-2 h-10 rounded-full pl-0.5 pr-0.5 lg:pr-3 hover:bg-stone-100">
                  <span className="h-9 w-9 rounded-full bg-teal/15 border border-teal/30 flex items-center justify-center text-sm font-bold text-teal">
                    {(user.name || "?").trim().charAt(0).toUpperCase() || <User className="h-4 w-4" />}
                  </span>
                  <span className="hidden lg:inline text-sm font-semibold">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="text-sm">{user.name}</div>
                  <div className="text-xs text-stone-500 font-normal truncate">{user.email || user.phone}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "customer" ? ACCOUNT_LINKS.map((l) => (
                  <DropdownMenuItem key={l.key} data-testid={`menu-${l.key}`}
                    onClick={() => (l.site === "shop" ? navigate(l.path) : (window.location.href = `${siteUrl(l.site)}${l.path}`))}>{l.label}</DropdownMenuItem>
                )) : (
                  <DropdownMenuItem onClick={() => goTo(dashboardPath)} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {isCustomerContext && <div className="md:hidden px-4 pb-2.5">{searchBox("flex", "nav-search-phone")}</div>}

      {isCustomerContext && <MenuRow current={here} />}
      {isCustomerContext && suggestion && <PlaceQuestion suggestion={suggestion} label={locationLabel} onUse={acceptSuggestion} onKeep={keepPlace} />}
      <LocationChangeDialog open={locOpen || required} required={required} onOpenChange={(o) => { if (o || !required) setLocOpen(o); else { skipChoice(); setLocOpen(false); } }} setLocation={setLocation} user={user} />
    </header>
  );
}

/** After sign-in: the place picked as a guest differs from the saved address. */
function PlaceQuestion({ suggestion, label, onUse, onKeep }) {
  return (
    <div className="border-t border-teal/20 bg-teal/5" data-testid="place-question">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        <span className="min-w-0">Deliver to your saved <b>{suggestion.label || "address"}</b>{suggestion.area ? ` in ${suggestion.area}` : ""} instead?</span>
        <span className="flex gap-2">
          <button type="button" onClick={onUse} className="rounded-full bg-teal text-white px-3 py-1 font-semibold" data-testid="place-use-saved">Use {suggestion.label || "it"}</button>
          <button type="button" onClick={onKeep} className="rounded-full border border-teal/40 bg-white text-teal px-3 py-1 font-semibold" data-testid="place-keep">Keep {label}</button>
        </span>
      </div>
    </div>
  );
}

function LocationChangeDialog({ open, onOpenChange, setLocation, user, required = false }) {
  const { coords, locationLabel, addressId, isDefault } = useLocation();
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [saved, setSaved] = useState([]);

  useEffect(() => { if (open) setSearch(""); }, [open]);
  const applyPin = (p) => { setLocation({ pincode: p }); done(p); };
  useEffect(() => {
    if (!open || user?.role !== "customer") { setSaved([]); return; }
    api.get("/customer/addresses").then(({ data }) => setSaved((data || []).filter(a => a.lat != null))).catch(() => setSaved([]));
  }, [open, user]);

  const done = (label) => { toast.success(`Showing shops that deliver to ${label}`); onOpenChange(false); };

  const useCurrent = async () => {
    setBusy(true);
    try {
      const p = await currentPosition();
      const named = await nameForPoint(p);
      const label = named ? (named.area || named.city || named.pincode) : "Near you";
      const detail = named ? (named.formatted_address || [named.area, named.city, named.pincode].filter(Boolean).join(", ")) : null;
      const code = named?.pincode || null;
      setLocation({ pincode: code, lat: p.lat, lng: p.lng, label, detail, source: "current_location" });
      done(label);
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const current = coords
    ? {
        title: coords.source === "current_location" && coords.label !== "Near you" ? `Current location · ${coords.label}` : (coords.source === "current_location" ? "Your current location" : coords.label),
        detail: coords.detail || (coords.source === "address" ? "Saved address" : null),
      }
    : { title: isDefault ? `${locationLabel} (default area)` : locationLabel, detail: "Approximate area — set your exact location below" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={required ? "loc-required" : "loc-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{required ? "Where should we deliver?" : "Change delivery location"}</DialogTitle>
          {required && <p className="text-sm text-stone-600">We'll show the shops, services and NGOs near this place. You can change it any time from the top bar.</p>}
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          {!required && <div className="rounded-xl bg-teal/5 border border-teal/30 px-3 py-2 min-w-0" data-testid="loc-current">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-teal">Delivering to</div>
            <div className="text-sm font-semibold text-stoneWarm truncate">{current.title}</div>
            {current.detail && <div className="text-xs text-stone-500 truncate">{current.detail}</div>}
          </div>}
          {saved.length > 0 && (
            <div className="min-w-0">
              <div className="text-xs font-semibold text-stone-600 mb-1">Your saved addresses</div>
              <Select
                value={addressId || ""}
                onValueChange={(id) => {
                  const a = saved.find(x => x.id === id);
                  if (!a) return;
                  setLocation(addressLocation(a));
                  done(a.label);
                }}
              >
                <SelectTrigger className="h-11 w-full min-w-0" data-testid="loc-saved-select">
                  <SelectValue placeholder="Choose a saved address" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  {saved.map(a => (
                    <SelectItem key={a.id} value={a.id} data-testid={`loc-saved-${a.id}`}>
                      <span className="font-semibold">{a.label}</span>
                      <span className="text-xs text-stone-500"> — {[a.flat_no, a.address].filter(Boolean).join(", ").slice(0, 60)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <PlacesAutocomplete
            value={search}
            onChange={setSearch}
            near={coords}
            onPlaceSelected={(p) => {
              const label = p.area || p.name || p.city || "Selected location";
              setLocation({ pincode: p.pincode || null, lat: p.lat, lng: p.lng, label, detail: p.formatted_address, source: "search" });
              done(label);
            }}
            onPincode={applyPin}
            dataTestid="loc-search-input"
            placeholder="Search area, society, landmark or pincode…"
          />
          <Button variant="outline" onClick={useCurrent} disabled={busy} data-testid="loc-use-current-btn" className="w-full rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LocateFixed className="h-4 w-4 mr-2" />}
            {busy ? "Finding you…" : "Use my current location"}
          </Button>
          {required && (
            <button type="button" onClick={() => onOpenChange(false)} className="w-full text-center text-sm font-semibold text-teal hover:underline py-1" data-testid="loc-skip">
              Just looking? Skip for now
            </button>
          )}
          {user?.role === "customer" && (
            <p className="text-xs text-stone-500 text-center">
              To deliver to a new place, <Link to="/account?tab=profile" onClick={() => onOpenChange(false)} className="text-teal font-semibold hover:underline">add it as a saved address</Link>.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
